import React, { useMemo, useRef, useEffect, useState } from "react";
import { motion, AnimatePresence, useReducedMotion } from "framer-motion";
import { Play, Pause, X, Loader2 } from "lucide-react";
import { Link } from "react-router-dom";
import { useAudioPlayer } from "../lib/audioPlayer";
import { useAudioPeaks } from "../lib/useAudioPeaks";
import { audioAmplitude } from "../lib/audioAmplitude";

/* ------------------------------------------------------------------ */
/*  MiniPlayer  ·  Tech-Glow                                            */
/*  Floating glass pill with pulsing champagne border + SVG waveform.  */
/* ------------------------------------------------------------------ */

const BAR_COUNT = 40;

/* Deterministic pseudo-waveform per slug — until real peaks arrive. */
function useSlugWaveform(slug) {
  return useMemo(() => {
    const src = slug || "field-note";
    let seed = 0;
    for (let i = 0; i < src.length; i++) seed = (seed * 31 + src.charCodeAt(i)) | 0;
    const rand = () => {
      seed = (seed * 1664525 + 1013904223) | 0;
      return (seed >>> 0) / 4294967295;
    };
    const bars = [];
    for (let i = 0; i < BAR_COUNT; i++) {
      const t = i / BAR_COUNT;
      // Base envelope: rises to middle, falls at the ends
      const env = Math.sin(Math.PI * t) * 0.55 + 0.25;
      // Voice-like modulation
      const mod =
        0.22 * Math.sin(t * 12.7 + rand() * 2) +
        0.15 * Math.sin(t * 27.3 + rand() * 3);
      bars.push(Math.min(1, Math.max(0.18, env + mod * rand())));
    }
    return bars;
  }, [slug]);
}

function formatTime(sec) {
  if (!sec || Number.isNaN(sec)) return "0:00";
  const m = Math.floor(sec / 60);
  const s = Math.floor(sec % 60);
  return `${m}:${s.toString().padStart(2, "0")}`;
}

export default function MiniPlayer() {
  const {
    current,
    status,
    progress,
    pause,
    resume,
    close,
    seek,
    audioElement,
  } = useAudioPlayer();
  const reduced = useReducedMotion();

  const [hover, setHover] = useState(false);
  const [amp, setAmp] = useState(0); // 0..1
  const analyserRef = useRef(null);
  const rafRef = useRef(null);
  const contextRef = useRef(null);
  const sourceRef = useRef(null);

  const slug = current?.slug || "";
  const pseudoBars = useSlugWaveform(slug);
  const audioSrc = audioElement?.currentSrc || audioElement?.src || null;
  const realBars = useAudioPeaks(audioSrc, BAR_COUNT);
  const bars = realBars || pseudoBars;
  const barsAreReal = !!realBars;

  // Wire up Web Audio analyser to the underlying <audio> element.
  useEffect(() => {
    if (!audioElement || reduced) return undefined;
    let cleanup = () => {};
    try {
      const AC = window.AudioContext || window.webkitAudioContext;
      if (!AC) return undefined;
      if (!contextRef.current) contextRef.current = new AC();
      const ctx = contextRef.current;
      if (!sourceRef.current) {
        try {
          sourceRef.current = ctx.createMediaElementSource(audioElement);
        } catch (e) {
          // MediaElement can only be piped once. If already piped, skip.
          return undefined;
        }
      }
      if (!analyserRef.current) {
        const analyser = ctx.createAnalyser();
        analyser.fftSize = 128;
        analyser.smoothingTimeConstant = 0.75;
        sourceRef.current.connect(analyser);
        analyser.connect(ctx.destination);
        analyserRef.current = analyser;
      }
      const analyser = analyserRef.current;
      const data = new Uint8Array(analyser.frequencyBinCount);
      const tick = () => {
        analyser.getByteFrequencyData(data);
        let sum = 0;
        // Low-mid range where the voice sits
        const lo = 2;
        const hi = Math.min(28, data.length);
        for (let i = lo; i < hi; i++) sum += data[i];
        const avg = sum / (hi - lo) / 255;
        setAmp((prev) => {
          const smoothed = prev * 0.55 + avg * 0.45;
          audioAmplitude.set(smoothed);
          return smoothed;
        });
        rafRef.current = window.requestAnimationFrame(tick);
      };
      if (status === "playing") {
        try {
          ctx.resume();
        } catch (e) {
          /* noop */
        }
        rafRef.current = window.requestAnimationFrame(tick);
      } else {
        // Drain amplitude to 0 when not playing so ScrollWords stops breathing.
        audioAmplitude.set(0);
      }
      cleanup = () => {
        if (rafRef.current) window.cancelAnimationFrame(rafRef.current);
      };
    } catch (e) {
      /* noop */
    }
    return cleanup;
  }, [audioElement, status, reduced]);

  if (!current) return null;

  const isPlaying = status === "playing";
  const isLoading = status === "loading";
  const pct = progress.duration
    ? Math.min(1, progress.currentTime / progress.duration)
    : 0;
  const litBars = Math.floor(pct * BAR_COUNT);

  // Border pulse: base idle breathing PLUS live amp reactivity.
  // Idle: ~4s cycle. Playing: overlay ampBoost.
  const idlePulse = isPlaying ? 0 : 1;
  const ampBoost = isPlaying ? amp : 0;
  const borderOpacity = 0.28 + ampBoost * 0.55; // 0.28 → 0.83
  const outerBlur = 12 + ampBoost * 22; // 12px → 34px
  const outerOpacity = 0.12 + ampBoost * 0.45; // 0.12 → 0.57
  const hoverBoost = hover ? 8 : 0;

  const onSeekBar = (idx) => {
    if (progress.duration) seek((idx / BAR_COUNT) * progress.duration);
  };

  const toggle = () => {
    if (isPlaying) pause();
    else resume();
  };

  return (
    <AnimatePresence>
      <motion.div
        initial={{ y: 60, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        exit={{ y: 60, opacity: 0 }}
        transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
        onMouseEnter={() => setHover(true)}
        onMouseLeave={() => setHover(false)}
        className="fixed bottom-4 left-4 right-4 md:left-6 md:right-auto md:max-w-[500px] z-40"
        data-testid="mini-player"
      >
        {/* Outer glow layer */}
        <motion.div
          aria-hidden
          className="absolute -inset-1 rounded-full pointer-events-none"
          animate={{
            opacity: idlePulse * (hover ? 0.5 : 0.3) + ampBoost * 0.6,
            scale: isPlaying ? 1 : reduced ? 1 : [1, 1.02, 1],
          }}
          transition={
            isPlaying
              ? { duration: 0.1 }
              : { duration: 4, repeat: Infinity, ease: [0.4, 0, 0.6, 1] }
          }
          style={{
            boxShadow: `0 0 ${outerBlur + hoverBoost}px rgba(201,169,97,${outerOpacity + (hover ? 0.1 : 0)})`,
          }}
        />

        {/* The pill */}
        <div
          className="relative flex items-center gap-3 rounded-full backdrop-blur-xl px-3 py-2 overflow-hidden"
          style={{
            background: "rgba(0, 0, 0, 0.55)",
            border: `1px solid rgba(201, 169, 97, ${borderOpacity})`,
            boxShadow: "inset 0 0 20px rgba(201,169,97,0.06)",
          }}
        >
          {/* Top-edge champagne highlight */}
          <span
            aria-hidden
            className="absolute inset-x-8 top-0 h-px"
            style={{
              background:
                "linear-gradient(90deg, transparent, rgba(201,169,97,0.6), transparent)",
              opacity: 0.5 + ampBoost * 0.4,
            }}
          />

          {/* Play / pause disc */}
          <button
            onClick={toggle}
            aria-label={isPlaying ? "Pause" : "Play"}
            data-testid="mini-player-toggle"
            className="relative flex h-10 w-10 shrink-0 items-center justify-center rounded-full border border-brass/60 text-brass hover:text-bg hover:bg-brass transition-colors duration-500"
            style={{ transitionTimingFunction: "cubic-bezier(0.2, 0.8, 0.2, 1)" }}
          >
            {isLoading ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : isPlaying ? (
              <Pause className="h-4 w-4" />
            ) : (
              <Play className="h-4 w-4 translate-x-[1px]" />
            )}
            {/* Play ring pulse */}
            {isPlaying && !reduced && (
              <motion.span
                aria-hidden
                className="absolute inset-0 rounded-full border border-brass/50 pointer-events-none"
                animate={{ scale: [1, 1.55], opacity: [0.7, 0] }}
                transition={{ duration: 2, repeat: Infinity, ease: "easeOut" }}
              />
            )}
          </button>

          {/* Title + kicker */}
          <div className="min-w-0 max-w-[130px] hidden sm:block">
            <p className="font-sans text-[9px] uppercase tracking-mega text-brass leading-none">
              Field note
            </p>
            <Link
              to={`/field-notes/${current.slug}`}
              className="mt-1 block font-serif text-[13px] text-ink leading-tight truncate hover:text-brass transition-colors"
              data-testid="mini-player-title"
            >
              {current.title}
            </Link>
          </div>

          {/* Waveform */}
          <div className="flex-1 min-w-0 flex items-center h-8">
            <svg
              viewBox={`0 0 ${BAR_COUNT * 5} 32`}
              preserveAspectRatio="none"
              className="w-full h-full"
              data-testid="mini-player-waveform"
              style={{
                opacity: barsAreReal ? 1 : 0.75,
                transition: "opacity 500ms cubic-bezier(0.2, 0.8, 0.2, 1)",
              }}
            >
              {bars.map((h, i) => {
                const isLit = i <= litBars;
                const isPlayhead = i === litBars;
                const heightPx = h * 26 + 4;
                // Amplitude reactivity on the playhead bars
                const dynBoost =
                  isPlaying && isPlayhead
                    ? 1 + amp * 0.9
                    : isPlaying && Math.abs(i - litBars) === 1
                      ? 1 + amp * 0.45
                      : 1;
                const finalH = Math.min(30, heightPx * dynBoost);
                const y = (32 - finalH) / 2;
                return (
                  <rect
                    key={i}
                    x={i * 5}
                    y={y}
                    width={3}
                    height={finalH}
                    rx={1.5}
                    fill={isLit ? "#C9A961" : "#F5F1E8"}
                    fillOpacity={isLit ? (isPlayhead ? 1 : 0.9) : 0.14}
                    style={{
                      filter: isPlayhead && isPlaying ? "drop-shadow(0 0 3px #F5D896)" : "none",
                      transition: "fill-opacity 300ms cubic-bezier(0.2, 0.8, 0.2, 1), height 120ms linear",
                      cursor: "pointer",
                    }}
                    onClick={() => onSeekBar(i)}
                  />
                );
              })}
            </svg>
          </div>

          {/* Time */}
          <div className="hidden md:block font-sans text-[10px] text-ink-muted tabular-nums leading-none shrink-0">
            {formatTime(progress.currentTime)}
            <span className="text-ink-muted/50"> / {formatTime(progress.duration)}</span>
          </div>

          {/* Close */}
          <button
            onClick={close}
            aria-label="Close player"
            data-testid="mini-player-close"
            className="h-7 w-7 shrink-0 flex items-center justify-center rounded-full text-ink-muted hover:text-ink hover:bg-white/5 transition-colors"
          >
            <X className="h-3.5 w-3.5" />
          </button>
        </div>
      </motion.div>
    </AnimatePresence>
  );
}
